import dynamic from 'next/dynamic';
import { memo } from 'react';

import { Skeleton } from '@air/component-skeleton';

import { usePDFOutlineContext } from '~/components/AssetModalV2/Content/Footer/contexts/PDFOutlineContext';

// Dynamic import of the PDF outline components to avoid SSR issues
const PDFViewerOutline = dynamic(
  () =>
    import('@air/react-pdfjs-viewer/src/components/PDFViewerOutline/PDFViewerOutline').then(
      (mod) => mod.PDFViewerOutline,
    ),
  {
    ssr: false,
    loading: () => (
      <div className="flex items-center justify-center p-4 ads:text-metadata text-gray-10">Loading outline...</div>
    ),
  },
);

export const PDFOutlinePanel = memo(() => {
  const { currentPage, numPages, changePage, pdfDocument, pdfPagesCacheRef, renderOutlinePageMenu } =
    usePDFOutlineContext();

  // Only render if we have PDF data - panel visibility is controlled elsewhere
  if (!pdfDocument || !pdfPagesCacheRef) {
    return (
      <div className="size-full overflow-hidden">
        <div className="mb-2 font-semibold ads:text-metadata text-14 text-gray-10">PDF Pages</div>
        <div className="h-[calc(100%-2rem)] overflow-hidden">
          <div className="scrollbar scrollbar-track-gray-2 scrollbar-thumb-gray-7 [&:not(:hover)]:scrollbar-track-transparent w-full flex-1 overflow-y-auto">
            <div className="space-y-2 px-6 py-2">
              {/* Loading skeleton with 3 placeholder items */}
              {Array.from({ length: 3 }).map((_, index) => (
                <div
                  key={index}
                  className="flex h-[108px] w-full flex-col items-center justify-center rounded-4 ads:bg-base bg-gray-1 px-0.5 pt-0.5"
                >
                  <div className="flex size-full items-center justify-center rounded-4 bg-black">
                    <Skeleton width={64} height={92} variant="square" />
                  </div>
                  <div className="py-2">
                    <Skeleton width={48} height={12} variant="square" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="size-full overflow-hidden">
      <div className="h-[calc(100%-2rem)] overflow-hidden">
        <PDFViewerOutline
          pagesCount={numPages}
          pdfPageCacheRef={pdfPagesCacheRef}
          isOpen={true} // Always open when this component is rendered
          currentPage={currentPage}
          onPageClick={changePage}
          renderOutlinePageMenu={renderOutlinePageMenu}
        />
      </div>
    </div>
  );
});

PDFOutlinePanel.displayName = 'PDFOutlinePanel';
